import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  FiUser,
  FiMail,
  FiPhone,
  FiMapPin,
  FiShield,
  FiLock,
  FiArrowLeft,
  FiCamera,
  FiTrash2,
  FiCheckCircle,
  FiSave,
  FiXCircle
} from "react-icons/fi";
import { ToastContainer, toast } from "react-toastify";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import methodModel from "../../methods/methods";
import { login_success } from "../../actions/user";
import Layout from "../../components/global/layout";
import FormControl from "../../components/common/FormControl";

export default function EditProfile() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const reduxUser = useSelector((state) => state.user);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    mobileNo: "",
    address: "",
    image: "",
    roleName: "Super Admin",
    userId: "",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Load super admin profile
  const loadProfile = useCallback(async () => {
    setInitialLoading(true);
    loader(true);
    try {
      let userData = { ...reduxUser };
      const res = await ApiClient.get("user/admin-profile");
      if (res?.success && (res?.data || res?.user_data)) {
        userData = { ...userData, ...(res.data || res.user_data) };
      }

      setForm({
        fullName: userData?.fullName || (userData?.firstName ? `${userData.firstName} ${userData.lastName || ""}` : "") || "Super Admin",
        email: userData?.email || "superadmin@yopmail.com",
        mobileNo: userData?.mobileNo || "",
        address: userData?.address || "",
        image: userData?.image || "",
        roleName: userData?.role?.name || "Super Admin",
        userId: userData?._id || userData?.id || reduxUser?._id || reduxUser?.id || "",
      });
    } catch (err) {
      console.error("Failed to load admin profile:", err);
      setForm({
        fullName: reduxUser?.fullName || "Super Admin",
        email: reduxUser?.email || "superadmin@yopmail.com",
        mobileNo: reduxUser?.mobileNo || "",
        address: reduxUser?.address || "",
        image: reduxUser?.image || "",
        roleName: reduxUser?.role?.name || "Super Admin",
        userId: reduxUser?._id || reduxUser?.id || "",
      });
    } finally {
      setInitialLoading(false);
      loader(false);
    }
  }, [reduxUser]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload a valid image file (JPG, PNG, or WEBP).");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size exceeds 5MB. Please choose a smaller image.");
      return;
    }

    setUploadingImage(true);
    loader(true);
    try {
      const res = await ApiClient.postFormData("upload/image", { file });
      const uploadedFileName = res?.fileName || res?.data?.fileName || (typeof res === "string" ? res : null);
      if (uploadedFileName) {
        setForm((prev) => ({ ...prev, image: uploadedFileName }));
        toast.success("Profile photo uploaded. Click 'Save Changes' to apply.");
      } else {
        toast.error("Failed to upload image. Please try again.");
      }
    } catch (err) {
      console.error("Image upload error:", err);
      toast.error("Failed to upload profile photo.");
    } finally {
      setUploadingImage(false);
      loader(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveImage = () => {
    setForm((prev) => ({ ...prev, image: "" }));
    toast.info("Photo removed. Click 'Save Changes' to apply.");
  };

  // Form validation
  const validateForm = () => {
    const errs = {};
    if (!form.fullName || !form.fullName.trim()) {
      errs.fullName = "Full Name is required.";
    } else if (form.fullName.trim().length < 2) {
      errs.fullName = "Full Name must be at least 2 characters.";
    }

    if (form.mobileNo && form.mobileNo.replace(/\D/g, "").length > 0 && form.mobileNo.replace(/\D/g, "").length < 8) {
      errs.mobileNo = "Please enter a valid phone number (min 8 digits).";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Submit profile changes
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix the validation errors before saving.");
      return;
    }

    const payload = {
      id: form.userId || reduxUser?._id,
      fullName: form.fullName.trim(),
      mobileNo: form.mobileNo,
      address: form.address,
      image: form.image,
    };

    setSubmitting(true);
    loader(true);
    try {
      const res = await ApiClient.put("user/profile", payload);
      if (res?.success || res?.status === 200 || res?.message?.toLowerCase().includes("success")) {
        const updatedUser = {
          ...reduxUser,
          ...payload,
        };
        dispatch(login_success(updatedUser));
        toast.success(res?.message || "Profile updated successfully!");
        setTimeout(() => {
          navigate("/profile");
        }, 800);
      } else {
        toast.error(res?.message || "Failed to update profile.");
      }
    } catch (err) {
      console.error("Profile update error:", err);
      toast.error(err?.message || "An error occurred while saving profile changes.");
    } finally {
      setSubmitting(false);
      loader(false);
    }
  };

  return (
    <Layout>
      <div className="w-full max-w-full overflow-x-hidden space-y-6 pb-12 antialiased">
        {/* ======================================================== */}
        {/* 1. TOP BREADCRUMB & PAGE HEADER */}
        {/* ======================================================== */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-2 border-b border-slate-200">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 mb-1">
              <Link to="/dashboard" className="hover:text-[#115B9D] transition-colors">
                Dashboard
              </Link>
              <span>/</span>
              <Link to="/profile" className="hover:text-[#115B9D] transition-colors">
                Profile
              </Link>
              <span>/</span>
              <span className="text-slate-800 font-bold">Edit Profile</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight flex items-center gap-2.5">
              <FiUser className="w-7 h-7 text-[#115B9D]" />
              Edit Profile
            </h1>
            <p className="text-xs sm:text-sm text-slate-600 font-normal mt-0.5">
              Update your super administrator identity, contact details, and account credentials.
            </p>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap self-stretch sm:self-auto">
            <Link
              to="/profile"
              className="px-4 py-2 rounded-xl text-xs font-bold text-slate-700 bg-white hover:bg-slate-50 transition-all border border-slate-200 shadow-xs flex items-center gap-1.5 cursor-pointer"
            >
              <FiArrowLeft className="w-3.5 h-3.5" />
              <span>Back to Profile</span>
            </Link>

            <Link
              to="/change-password"
              className="px-4 py-2 rounded-xl text-xs font-bold text-slate-800 bg-slate-100 hover:bg-slate-200 transition-all border border-slate-200 shadow-xs flex items-center gap-1.5 cursor-pointer"
            >
              <FiLock className="w-3.5 h-3.5 text-[#115B9D]" />
              <span>Change Password</span>
            </Link>
          </div>
        </div>

        {/* ======================================================== */}
        {/* 2. MAIN EDIT FORM */}
        {/* ======================================================== */}
        <form onSubmit={handleSubmit} noValidate>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

            {/* Left Column: Avatar & Photo Controls (4 cols) */}
            <div className="lg:col-span-4 space-y-6">
              <div className="bg-white rounded-2xl p-6 border border-slate-200/90 shadow-[0_2px_10px_rgba(0,0,0,0.03)] text-center">
                <div className="flex items-center justify-between pb-3.5 border-b border-slate-100 mb-5">
                  <h3 className="text-base font-black text-slate-900 tracking-tight">
                    Profile Picture
                  </h3>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                    {form.roleName}
                  </span>
                </div>

                {/* Avatar Preview */}
                <div className="relative w-32 h-32 mx-auto mb-4 group">
                  {form.image ? (
                    <img
                      src={methodModel.userImg(form.image)}
                      alt={form.fullName}
                      className="w-32 h-32 rounded-2xl object-cover border-4 border-slate-100 shadow-lg bg-slate-100"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-2xl bg-gradient-to-br from-[#115B9D] to-[#0A3E6E] text-white flex items-center justify-center text-4xl font-black shadow-lg border-4 border-slate-100">
                      {(form.fullName || "A").charAt(0).toUpperCase()}
                    </div>
                  )}

                  <label
                    htmlFor="avatarUploadInput"
                    className="absolute -bottom-2 -right-2 w-9 h-9 rounded-xl bg-[#115B9D] text-white flex items-center justify-center shadow-lg hover:bg-[#0E528F] transition-all cursor-pointer border-2 border-white"
                    title="Upload new photo"
                  >
                    <FiCamera className="w-4 h-4" />
                  </label>
                </div>

                {/* Hidden File Input */}
                <input
                  ref={fileInputRef}
                  id="avatarUploadInput"
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  className="hidden"
                  onChange={handleImageUpload}
                  disabled={uploadingImage || submitting}
                />

                {/* Admin Dynamic Details (No dummy text!) */}
                <div className="mb-5">
                  <h4 className="text-lg font-black text-slate-900 truncate">
                    {form.fullName || "Super Admin"}
                  </h4>
                  <p className="text-xs text-slate-500 font-mono truncate mt-0.5">
                    {form.email}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-2.5">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage || submitting}
                    className="w-full py-2.5 px-4 rounded-xl text-xs font-bold text-white bg-[#115B9D] hover:bg-[#0E528F] transition-all shadow-sm flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
                  >
                    <FiCamera className="w-4 h-4" />
                    <span>{uploadingImage ? "Uploading..." : form.image ? "Change Photo" : "Upload Photo"}</span>
                  </button>

                  {form.image ? (
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      disabled={uploadingImage || submitting}
                      className="w-full py-2 px-4 rounded-xl text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 transition-all border border-red-200 flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-60"
                    >
                      <FiTrash2 className="w-3.5 h-3.5" />
                      <span>Remove Photo</span>
                    </button>
                  ) : null}
                </div>

                <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">
                  Allowed formats: JPG, PNG, or WEBP. Maximum file size: 5MB.
                </p>
              </div>

              {/* Quick Info Box */}
              <div className="bg-sky-50/70 rounded-2xl p-5 border border-sky-100 space-y-2 text-left">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-800">
                  <FiShield className="w-4 h-4 text-[#115B9D]" />
                  <span>Administrative Role</span>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">
                  You are editing the root Super Administrator account. All changes affect system logs and audit trails.
                </p>
              </div>
            </div>

            {/* Right Column: Platform Inputs Form (8 cols) */}
            <div className="lg:col-span-8 space-y-6">
              <div className="bg-white rounded-2xl p-6 sm:p-7 border border-slate-200/90 shadow-[0_2px_10px_rgba(0,0,0,0.03)] space-y-6">
                <div className="border-b border-slate-100 pb-4">
                  <h3 className="text-lg font-black text-slate-900 tracking-tight">
                    Basic Information
                  </h3>
                  <p className="text-xs text-slate-600 font-normal mt-0.5">
                    Update your official identity and primary contact records.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {/* Full Name (Platform Input) */}
                  <div className="sm:col-span-2">
                    <FormControl
                      type="text"
                      label="Full Name"
                      name="fullName"
                      placeholder="Enter full name"
                      value={form.fullName}
                      required={true}
                      error={errors.fullName}
                      onChange={(val) => {
                        setForm((prev) => ({ ...prev, fullName: val }));
                        if (errors.fullName) setErrors((prev) => ({ ...prev, fullName: "" }));
                      }}
                    />
                  </div>

                  {/* Primary Email (Platform Input - Disabled) */}
                  <div className="sm:col-span-1">
                    <FormControl
                      type="email"
                      label="Primary Email"
                      name="email"
                      value={form.email}
                      disabled={true}
                      placeholder="superadmin@yopmail.com"
                    />
                    <span className="text-[11px] text-slate-500 mt-1 block">
                      Email address cannot be changed directly for security reasons.
                    </span>
                  </div>

                  {/* Mobile Number (Platform Input) */}
                  <div className="sm:col-span-1">
                    <FormControl
                      type="phone"
                      label="Mobile Number"
                      name="mobileNo"
                      value={form.mobileNo}
                      placeholder="Enter mobile number"
                      error={errors.mobileNo}
                      onChange={(val) => {
                        setForm((prev) => ({ ...prev, mobileNo: val }));
                        if (errors.mobileNo) setErrors((prev) => ({ ...prev, mobileNo: "" }));
                      }}
                    />
                  </div>

                  {/* Office Address (Platform Input) */}
                  <div className="sm:col-span-2">
                    <FormControl
                      type="text"
                      label="Office / Physical Address"
                      name="address"
                      placeholder="Enter office or street address"
                      value={form.address}
                      onChange={(val) => {
                        setForm((prev) => ({ ...prev, address: val }));
                      }}
                    />
                  </div>
                </div>

                {/* Form Footer Action Buttons */}
                <div className="pt-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => navigate("/profile")}
                    disabled={submitting}
                    className="w-full sm:w-auto px-5 py-2.5 rounded-xl text-xs font-bold text-slate-700 bg-white hover:bg-slate-100 transition-all border border-slate-300 shadow-xs flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
                  >
                    <FiXCircle className="w-4 h-4" />
                    <span>Discard / Cancel</span>
                  </button>

                  <button
                    type="submit"
                    disabled={submitting || initialLoading}
                    className="w-full sm:w-auto px-6 py-2.5 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-[#115B9D] via-[#0E528F] to-[#0A3E6E] hover:from-[#0E528F] hover:to-[#08335C] transition-all shadow-md shadow-[#115B9D]/25 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
                  >
                    <FiSave className="w-4 h-4" />
                    <span>{submitting ? "Saving Changes..." : "Save Changes"}</span>
                  </button>
                </div>
              </div>
            </div>

          </div>
        </form>
      </div>
      <ToastContainer position="top-right" />
    </Layout>
  );
}
